import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faChevronDown,
  faSignOutAlt,
  faTimes,
  faBars,
  faTachometerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faFacebookF } from "@fortawesome/free-brands-svg-icons";
import { useAuth } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";

export const LangToggle: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const toggle = () => {
    const next = currentLang === "en" ? "bn" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
    const url = new URL(window.location.href);
    if (next === "en") {
      url.searchParams.delete("lang");
    } else {
      url.searchParams.set("lang", next);
    }
    window.history.replaceState(null, "", url.toString());
  };

  return (
    <button
      onClick={toggle}
      title="Switch language"
      className={`flex items-center gap-1 border border-white/60 hover:border-white rounded-full px-2 py-1 transition-all hover:bg-white/10 ${className}`}
    >
      <span className={`text-xs font-extrabold leading-none ${currentLang === "en" ? "text-white" : "text-white/50"}`}>
        EN
      </span>
      <span className="text-white/40 text-xs">|</span>
      <span className={`text-xs font-extrabold leading-none ${currentLang === "bn" ? "text-white" : "text-white/50"}`}>
        বাং
      </span>
    </button>
  );
};

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const { isAuthenticated, authChecked, student, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleUserMenu = () => setShowUserMenu((prev) => !prev);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsMenuOpen(false);
  };

  const navLinks = [
    { to: "/course/courses", label: t("nav.courses") },
    { to: "/ausbildung", label: t("nav.findAusbildung") },
    { to: "/fsj-bfd-circular", label: t("nav.fsjBfdCircular") },
    { to: "/pages/check-eligibility", label: t("nav.checkEligibility") },
    // Book Appointment is on the homepage; Blog, Contact and About live in the footer
  ] as { to: string; label: string; mobileOnly?: boolean }[];

  const dashboardPath =
    student?.role === "ADMIN"
      ? "/admin/dashboard"
      : student?.role === "MENTOR"
        ? "/mentor/dashboard"
        : student?.role === "EMPLOYEE"
          ? "/employee/dashboard"
          : "/student/student-dashboard";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 5);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    document.body.style.position = isMenuOpen ? "fixed" : "";
    document.body.style.width = isMenuOpen ? "100%" : "";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isMenuOpen]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) {
      setIsMenuOpen(false);
    }
  };

  const isActive = (path: string) =>
    location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`));

  return (
    <>
      <section
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#008ac7] shadow-lg py-3"
            : "bg-[#009fe3] shadow-md py-4"
        } text-white`}
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-xl md:text-2xl font-extrabold tracking-tight flex items-center"
          >
            <span className="text-black">Guideline</span>
            <span className="text-red-600">2</span>
            <span className="text-yellow-400">Germany</span>
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center ml-6 space-x-4 xl:space-x-5 text-sm font-semibold whitespace-nowrap">
            {navLinks.filter((item) => !item.mobileOnly).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`hover:underline ${
                  isActive(item.to) ? "font-bold underline" : ""
                }`}
              >
                {item.label}
              </Link>
            ))}

            <a
              href="https://www.facebook.com/guideline2germany"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:flex items-center justify-center w-8 h-8 border-2 border-white rounded-full hover:bg-white hover:text-[#009fe3] transition-all duration-300 transform hover:scale-105"
            >
              <FontAwesomeIcon
                icon={faFacebookF}
                style={{ fontSize: "18px" }}
              />
            </a>

            <LangToggle />

            {!authChecked ? (
              // Same size as the Sign in button, so nothing jumps when the check finishes
              <span className="invisible px-5 py-2" aria-hidden="true">{t("nav.signIn")}</span>
            ) : !isAuthenticated ? (
              <Link
                to="/login"
                className={`bg-white text-[#009fe3] font-semibold px-5 py-2 rounded-full hover:bg-gray-100 transition-colors shadow-sm ${
                  isActive("/login") ? "ring-2 ring-yellow-300" : ""
                }`}
              >
                {t("nav.signIn")}
              </Link>
            ) : (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors px-3 py-1.5 rounded-full"
                >
                  <div className="w-7 h-7 rounded-full bg-white text-[#009fe3] flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {student?.firstName?.[0]?.toUpperCase() ?? "U"}
                  </div>
                  <span className="font-semibold text-sm">{student?.firstName || "User"}</span>
                  <FontAwesomeIcon icon={faChevronDown} className="text-xs opacity-70" />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-52 bg-white text-gray-800 shadow-xl rounded-xl overflow-hidden border border-gray-100">
                    {/* User info header */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {student?.firstName} {student?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 capitalize mt-0.5">
                        {student?.role?.toLowerCase()}
                      </p>
                    </div>
                    <Link
                      to={dashboardPath}
                      className={`flex items-center gap-2.5 px-4 py-2.5 hover:bg-blue-50 hover:text-[#009fe3] transition-colors text-sm font-medium ${
                        isActive(dashboardPath) ? "bg-blue-50 text-[#009fe3]" : ""
                      }`}
                    >
                      <FontAwesomeIcon icon={faTachometerAlt} className="text-xs w-4" />
                      <span>{t("nav.dashboard")}</span>
                    </Link>
                    <div className="border-t border-gray-100" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-2.5 text-sm font-medium text-gray-600"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="text-xs w-4" />
                      <span>{t("nav.logout")}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Mobile Buttons */}
          <div className="flex items-center space-x-3 lg:hidden flex-shrink-0">
            <a
              href="https://www.facebook.com/guideline2germany"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-8 h-8 border-2 border-white rounded-full hover:bg-white hover:text-[#009fe3] transition-all duration-300 transform hover:scale-105"
            >
              <FontAwesomeIcon
                icon={faFacebookF}
                style={{ fontSize: "18px" }}
              />
            </a>
            <LangToggle />
            <button
              onClick={() => setIsMenuOpen(true)}
              className="focus:outline-none text-white"
            >
              <FontAwesomeIcon icon={faBars} className="text-xl" />
            </button>
          </div>
        </div>
      </section>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          ref={overlayRef}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-[60] bg-gray-500 bg-opacity-30 lg:hidden"
        >
          <div className="fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-out">
            <div className="flex flex-col h-full">
              <div className="bg-[#009fe3] px-6 flex items-center justify-between shadow-md py-4">
                <div className="text-2xl font-extrabold tracking-tight">
                  <span className="text-black">Guideline</span>
                  <span className="text-red-600">2</span>
                  <span className="text-yellow-400">Germany</span>
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-2xl" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto py-2">
                {navLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-6 py-4 text-base font-medium transition-all duration-200 border-l-4 ${
                      isActive(item.to)
                        ? "border-[#009fe3] bg-blue-50 text-[#009fe3]"
                        : "border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
                {isAuthenticated ? (
                  <>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="bg-[#009fe3] rounded-full p-2">
                          <FontAwesomeIcon
                            icon={faUserCircle}
                            className="text-2xl text-white"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {student?.firstName} {student?.lastName}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {student?.role?.toLowerCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={dashboardPath}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center justify-center space-x-2 w-full bg-[#009fe3] text-white font-semibold py-3 rounded-lg hover:bg-[#008ac7] transition-colors shadow-md"
                    >
                      <FontAwesomeIcon icon={faTachometerAlt} />
                      <span>{t("nav.dashboard")}</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center justify-center space-x-2 w-full bg-red-500 text-white font-semibold py-3 rounded-lg hover:bg-red-600 transition-colors shadow-md"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>{t("nav.logout")}</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-center bg-[#009fe3] text-white font-semibold py-3 rounded-lg hover:bg-[#008ac7] transition-colors shadow-md"
                  >
                    {t("nav.signIn")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="h-24" />
    </>
  );
};

export default Navbar;
